import { loadPagedInfiniteListUntil } from './progressiveList';
import type { Root } from './prototypeTypes';

const RETURN_ANCHOR_MAX_FRAMES = 18;
const RETURN_ANCHOR_STABLE_FRAMES = 3;
const RETURN_ANCHOR_TOLERANCE_PX = 0.5;
const RETURN_FONT_WAIT_MS = 250;

export interface HomeReturnState {
	containerId: 'recommended-list' | 'mobileTrendingFeed' | 'homeTopicTrendingRail' | 'homeTopicNewestRail' | 'recent-list' | 'favorites-list';
	cardSlug: string;
	cardIndex: number;
	windowScrollY: number;
	railScrollLeft: number;
	targetViewportTop?: number;
}

const HOME_CARD_SURFACES: Array<{ id: HomeReturnState['containerId']; cardSelector: string }> = [
	{ id: 'recommended-list', cardSelector: '.topic-card' },
	{ id: 'mobileTrendingFeed', cardSelector: '.topic-card' },
	{ id: 'homeTopicTrendingRail', cardSelector: '.topic-card' },
	{ id: 'homeTopicNewestRail', cardSelector: '.topic-card' },
	{ id: 'recent-list', cardSelector: '.recent-card' },
	{ id: 'favorites-list', cardSelector: '.home-saved-topic-card' },
];

/** Captures only a Home card departure; unrelated Topic entry points remain untouched. */
export function captureHomeReturnState(root: Root, source: HTMLElement, topicSlug: string): HomeReturnState | null {
	if (!topicSlug || !root.querySelector('#view-home.active')) return null;
	for (const surface of HOME_CARD_SURFACES) {
		const container = root.querySelector<HTMLElement>(`#${surface.id}`);
		const card = source.closest<HTMLElement>(surface.cardSelector);
		if (!container || !card || !container.contains(card)) continue;
		const cards = Array.from(container.querySelectorAll<HTMLElement>(surface.cardSelector));
		const cardIndex = cards.indexOf(card);
		if (cardIndex < 0) return null;
		const targetViewportTop = card.getBoundingClientRect().top;
		if (!isViewportCoordinate(targetViewportTop)) return null;
		return {
			containerId: surface.id,
			cardSlug: topicSlug,
			cardIndex,
			windowScrollY: Math.max(0, window.scrollY || 0),
			railScrollLeft: Math.max(0, container.scrollLeft || 0),
			targetViewportTop,
		};
	}
	return null;
}

/**
 * Replays a return after Home has finished its data hydration. The state is
 * consumed in all outcomes, preventing a direct Home visit from replaying an
 * old departure after a browser session restoration.
 */
export async function restoreHomeReturnState(root: Root): Promise<void> {
	if (typeof window === 'undefined' || !window.history) return;
	const state = homeReturnStateFromHistory(window.history.state);
	if (!state || !root.querySelector('#view-home.active')) return;
	try {
		const container = root.querySelector<HTMLElement>(`#${state.containerId}`);
		if (!container) return;
		let card = homeCardForState(container, state);
		if (!card && state.containerId === 'recommended-list') {
			await loadPagedInfiniteListUntil<{ slug?: string }>(container, (items) => items.some((item) => item?.slug === state.cardSlug));
			card = homeCardForState(container, state);
		}
		if (!card) return;
		container.scrollLeft = state.railScrollLeft;
		window.scrollTo({ top: state.windowScrollY, behavior: 'instant' as ScrollBehavior });
		if (state.targetViewportTop !== undefined) await reanchorTargetUntilStable(card, state.targetViewportTop);
	} finally {
		clearHomeReturnState();
	}
}

export function homeReturnStateFromHistory(value: unknown): HomeReturnState | null {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
	const candidate = (value as Record<string, unknown>).homeReturnState;
	if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
	const record = candidate as Record<string, unknown>;
	const validSurface = HOME_CARD_SURFACES.some((surface) => surface.id === record.containerId);
	if (!validSurface || typeof record.cardSlug !== 'string' || record.cardSlug.length === 0 || record.cardSlug.length > 240) return null;
	if (!Number.isInteger(record.cardIndex) || (record.cardIndex as number) < 0) return null;
	if (!isNonNegativeNumber(record.windowScrollY) || !isNonNegativeNumber(record.railScrollLeft)) return null;
	if (record.targetViewportTop !== undefined && !isViewportCoordinate(record.targetViewportTop)) return null;
	return {
		containerId: record.containerId as HomeReturnState['containerId'],
		cardSlug: record.cardSlug,
		cardIndex: record.cardIndex as number,
		windowScrollY: record.windowScrollY as number,
		railScrollLeft: record.railScrollLeft as number,
		...(record.targetViewportTop === undefined ? {} : { targetViewportTop: record.targetViewportTop as number }),
	};
}

function homeCardForState(container: HTMLElement, state: HomeReturnState): HTMLElement | null {
	const cards = Array.from(container.querySelectorAll<HTMLElement>('.topic-card, .recent-card'));
	return cards.find((card) => card.dataset.id === state.cardSlug) ?? null;
}

/** Discards an unusable return marker before a normal Home refresh. */
export function clearHomeReturnState(): void {
	if (typeof window === 'undefined' || !window.history) return;
	const current = window.history.state && typeof window.history.state === 'object' ? window.history.state as Record<string, unknown> : {};
	if (!Object.hasOwn(current, 'homeReturnState')) return;
	const { homeReturnState: _consumed, ...withoutHomeReturnState } = current;
	window.history.replaceState(withoutHomeReturnState, '', window.location.href);
}

function isNonNegativeNumber(value: unknown): value is number {
	return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isViewportCoordinate(value: unknown): value is number {
	return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 1_000_000;
}

async function reanchorTargetUntilStable(card: HTMLElement, targetViewportTop: number): Promise<void> {
	await waitForDocumentFonts();
	if (!isActiveHomeCard(card)) return;
	let stableFrames = 0;
	for (let frame = 0; frame < RETURN_ANCHOR_MAX_FRAMES; frame += 1) {
		await afterAnimationFrame();
		if (!isActiveHomeCard(card)) return;
		if (reanchorTargetCard(card, targetViewportTop)) {
			stableFrames += 1;
			if (stableFrames >= RETURN_ANCHOR_STABLE_FRAMES) return;
		} else {
			stableFrames = 0;
		}
	}
}

function isActiveHomeCard(card: HTMLElement): boolean {
	return card.isConnected && card.closest('#view-home.active') !== null;
}

async function waitForDocumentFonts(): Promise<void> {
	const fontsReady = document.fonts?.ready;
	if (!fontsReady) return;
	await new Promise<void>((resolve) => {
		const timeout = window.setTimeout(resolve, RETURN_FONT_WAIT_MS);
		void fontsReady.then(() => {
			window.clearTimeout(timeout);
			resolve();
		}, () => {
			window.clearTimeout(timeout);
			resolve();
		});
	});
}

function afterAnimationFrame(): Promise<void> {
	return new Promise<void>((resolve) => {
		if (typeof window.requestAnimationFrame === 'function') {
			window.requestAnimationFrame(() => resolve());
			return;
		}
		window.setTimeout(resolve, 0);
	});
}

function reanchorTargetCard(card: HTMLElement, targetViewportTop: number): boolean {
	const delta = card.getBoundingClientRect().top - targetViewportTop;
	if (Math.abs(delta) <= RETURN_ANCHOR_TOLERANCE_PX) return true;
	window.scrollBy({ top: delta, left: 0, behavior: 'instant' as ScrollBehavior });
	return false;
}
